// =====================================================================================
// VideoContext (Refactored) - Orchestrates video services for clean architecture
// =====================================================================================
// Acts as coordinator between LiveKit, state management, and reconnection logic
// Reduced from 675 lines to ~350 lines through service extraction

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useVideoPreferences } from '../hooks/useVideoPreferences.js';
import { useAppContext } from '../hooks/useAppContext.js';
import { LiveKitConnectionManager, VideoQualityPreset } from '../services/video/LiveKitConnectionManager.js';
import { performanceMonitor } from '../services/video/PerformanceMonitor.js';
import { VideoStateManager } from '../services/video/VideoStateManager.js';
import { ReconnectionStrategy } from '../services/video/ReconnectionStrategy.js';
import { videoErrorHandler } from '../services/video/VideoErrorHandler.js';
import { classifyMediaDeviceError } from '../services/video/media-device-errors.js';
import type { VideoError, VideoTokenResponse, DisconnectReason } from '../services/video/types.js';
import type { VideoPreferences } from '../hooks/useVideoPreferences.js';
import { RoomEvent } from 'livekit-client';
import type { Room } from 'livekit-client';
import type { PrivateAreaUpdateData } from '../../../shared/src/schemas/video.schema.js';

/**
 * Video context state interface providing access to video call functionality.
 * @interface VideoContextState
 */
interface VideoContextState {
  isConnected: boolean;
  room: Room | null;
  participants: number;
  /** What LiveKit is actually publishing once there is a room; the desired value before that. */
  micEnabled: boolean;
  /** What LiveKit is actually publishing once there is a room; the desired value before that. */
  cameraEnabled: boolean;
  /** True while a `setMicrophoneEnabled` call is in flight. */
  micPending: boolean;
  /** True while a `setCameraEnabled` call is in flight. */
  cameraPending: boolean;
  canControl: boolean;
  toggleMic: () => Promise<void>;
  toggleCamera: () => Promise<void>;
  currentPrivateArea: PrivateAreaUpdateData | null;
  error: VideoError | null;
  isReconnecting: boolean;
  connectionAttempts: number;
  maxReconnectAttempts: number;
  retryConnection: () => void;
  errorClassification: ReturnType<typeof videoErrorHandler.classifyError> | null;
  getUserFriendlyError: () => string | null;
  getRecoveryActions: () => ReturnType<typeof videoErrorHandler.getRecoveryActions>;
}

/**
 * React Context for video call functionality.
 * Provides access to LiveKit room, connection state, and video controls.
 * @example
 * ```tsx
 * const videoState = useContext(VideoContext);
 * if (videoState?.isConnected) {
 *   // Video is connected
 * }
 * ```
 */
export const VideoContext = createContext<VideoContextState | null>(null);

/**
 * Video Provider component that manages video call state and LiveKit connections.
 * Handles automatic connection to private areas, error recovery, and performance monitoring.
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that will have access to video context
 *
 * @example
 * ```tsx
 * <VideoProvider>
 *   <GameComponent />
 * </VideoProvider>
 * ```
 */
export const VideoProvider = React.memo(({ children }: { children: React.ReactNode }) => {
  const { auth, roomConnection } = useAppContext();
  const { preferences, setMicEnabled, setCameraEnabled } = useVideoPreferences();

  // Initialize services only once with lazy initialization
  const connectionManager = useRef<LiveKitConnectionManager | null>(null);
  const stateManager = useRef<VideoStateManager | null>(null);
  const reconnectionStrategy = useRef<ReconnectionStrategy | null>(null);

  // Lazy initialize services only on first render
  if (!connectionManager.current) {
    connectionManager.current = new LiveKitConnectionManager(VideoQualityPreset.ADAPTIVE);
  }
  if (!stateManager.current) {
    stateManager.current = new VideoStateManager();
  }
  if (!reconnectionStrategy.current) {
    reconnectionStrategy.current = new ReconnectionStrategy();
  }

  // Local state for UI updates
  const [error, setError] = useState<VideoError | null>(null);
  const [errorClassification, setErrorClassification] = useState<ReturnType<typeof videoErrorHandler.classifyError> | null>(null);
  const [videoState, setVideoState] = useState(() => stateManager.current!.getState());

  // What LiveKit is really publishing. Never written from a preference — only
  // ever read back off `localParticipant`, so the buttons cannot lie (#75).
  const [publishedMedia, setPublishedMedia] = useState({ micEnabled: false, cameraEnabled: false });
  const [micPending, setMicPending] = useState(false);
  const [cameraPending, setCameraPending] = useState(false);
  // Mirrors of the pending flags, so a second click in the same tick is dropped
  // before React has re-rendered the disabled button.
  const micPendingRef = useRef(false);
  const cameraPendingRef = useRef(false);

  // Timeout refs
  const tokenTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const autoConnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // The live `connectToVideoRoom`. The automatic retry path and the connection
  // manager's callbacks are installed once, but `connectToVideoRoom` is rebuilt
  // whenever the socket changes — and this provider mounts at the app root
  // *before* login, when `roomConnection` still carries a null socket. Calling
  // the copy captured then returns immediately, which made every automatic
  // reconnect a silent no-op (#77). Go through the ref instead.
  const connectToVideoRoomRef = useRef<(isRetry?: boolean, intent?: Partial<VideoPreferences>) => Promise<void>>(
    async () => {}
  );

  // The state the *next* connection should ask for. Kept in a ref because the
  // token round-trip outlives the render that started it: reading `preferences`
  // from a closure there sends the pre-toggle value to the backend (#75).
  const desiredPreferencesRef = useRef<VideoPreferences>(preferences);
  useEffect(() => {
    desiredPreferencesRef.current = preferences;
  }, [preferences]);

  // Read the truth off the local participant.
  const syncPublishedMedia = useCallback((room: Room | null) => {
    const local = room?.localParticipant;
    setPublishedMedia({
      micEnabled: local?.isMicrophoneEnabled ?? false,
      cameraEnabled: local?.isCameraEnabled ?? false
    });
  }, []);

  // Get player status from roomConnection data
  const playerStatus = roomConnection.playersData.find(p => p.userId === auth.user?.id)?.status || 'available';

  // Busy and Do Not Disturb take the media controls away — declared here rather
  // than at the bottom of the provider because the toggles themselves honour it.
  const canControl = playerStatus === 'available';

  // Latest status for the token round-trip, which outlives the render that started it
  const playerStatusRef = useRef(playerStatus);
  playerStatusRef.current = playerStatus;
  const previousStatusRef = useRef(playerStatus);
  // Whether the live connection's token was requested while Available. The
  // backend only grants `canPublish` then, so a call joined while Busy has to
  // reconnect before it can publish again.
  const connectionCanPublishRef = useRef(false);

  // Helper function to handle errors with classification
  const handleError = useCallback((errorData: VideoError) => {
    console.error('🎥 [CONTEXT] Handling video error:', errorData);

    const classification = videoErrorHandler.classifyError(errorData);
    console.info('🎥 [CONTEXT] Error classification:', {
      code: errorData.code,
      category: classification.category,
      severity: classification.severity,
      userMessage: classification.userMessage,
      shouldRetry: classification.shouldRetry
    });

    setError(errorData);
    setErrorClassification(classification);
  }, []);

  // Helper function to clear errors
  const clearError = useCallback(() => {
    setError(null);
    setErrorClassification(null);
  }, []);

  // Update state when state manager changes
  useEffect(() => {
    stateManager.current.onStateChange = (newState) => {
      setVideoState(newState);
    };
  }, []);

  // Follow the local participant's real publish state for as long as there is a
  // room. `RoomContext` lives inside the overlay, which is not always mounted,
  // so the subscription is made here rather than through
  // `@livekit/components-react` hooks.
  useEffect(() => {
    const room = videoState.room;
    if (!room) {
      setPublishedMedia({ micEnabled: false, cameraEnabled: false });
      return;
    }

    function sync() {
      syncPublishedMedia(room);
    }
    sync();

    room.on(RoomEvent.TrackMuted, sync);
    room.on(RoomEvent.TrackUnmuted, sync);
    room.on(RoomEvent.LocalTrackPublished, sync);
    room.on(RoomEvent.LocalTrackUnpublished, sync);
    room.on(RoomEvent.ConnectionStateChanged, sync);

    return () => {
      room.off(RoomEvent.TrackMuted, sync);
      room.off(RoomEvent.TrackUnmuted, sync);
      room.off(RoomEvent.LocalTrackPublished, sync);
      room.off(RoomEvent.LocalTrackUnpublished, sync);
      room.off(RoomEvent.ConnectionStateChanged, sync);
    };
  }, [videoState.room, syncPublishedMedia]);

  // Handle retry with reconnection strategy.
  //
  // Deliberately dependency-free, and therefore stable enough to be a
  // dependency of the effect below: everything it touches is a ref, including
  // the connection function itself.
  const handleRetry = useCallback(async () => {
    const attempts = stateManager.current.incrementConnectionAttempts();
    stateManager.current.setReconnecting(true);

    console.info(`🎥 Retrying connection (attempt ${attempts})`);

    // Disconnect existing connection
    await connectionManager.current.disconnect();

    // Schedule retry with exponential backoff
    reconnectionStrategy.current.scheduleRetry(() => {
      void connectToVideoRoomRef.current(true);
    }, attempts - 1);
  }, []);

  // Setup connection manager event handlers
  useEffect(() => {
    const manager = connectionManager.current;
    const state = stateManager.current;
    const strategy = reconnectionStrategy.current;

    manager.onConnected = (room) => {
      state.setConnected(true);
      state.setRoom(room);
      state.resetConnectionAttempts();
      state.setReconnecting(false);
      state.setConnectionInProgress(null);
      state.setCurrentRequestId(null);
      clearError();

      // The room is handed to us rather than fetched, so monitoring can no
      // longer be skipped by a null `getRoom()` during `connect()` (#77).
      performanceMonitor.startMonitoring(room);
    };

    manager.onDisconnected = (reason?: DisconnectReason) => {
      state.setConnected(false);
      state.setRoom(null);

      // Stop performance monitoring when disconnected
      performanceMonitor.stopMonitoring();

      // Parse disconnect reason
      const { shouldRetry, message } = ReconnectionStrategy.parseDisconnectReason(reason);
      const stillInArea = state.getState().currentPrivateAreaId !== null;

      // Handle unexpected disconnections
      if (reason && shouldRetry && stillInArea) {
        const errorData: VideoError = {
          code: 'UNEXPECTED_DISCONNECT',
          message,
          timestamp: Date.now()
        };
        handleError(errorData);

        // Check if should automatically retry based on error classification
        const attempts = state.getState().connectionAttempts;
        if (strategy.shouldRetry(attempts, errorData) && videoErrorHandler.shouldAutoRetry(errorData)) {
          handleRetry();
        }
      }
    };

    manager.onReconnecting = () => {
      state.setReconnecting(true);
    };

    manager.onReconnected = () => {
      state.setReconnecting(false);
      clearError();
    };

    manager.onParticipantConnected = () => {
      const room = manager.getRoom();
      if (room) {
        state.setParticipants(room.remoteParticipants?.size || 0);
      }
    };

    manager.onParticipantDisconnected = () => {
      const room = manager.getRoom();
      if (room) {
        state.setParticipants(room.remoteParticipants?.size || 0);
      }
    };
  }, [handleError, clearError, handleRetry]);

  /**
   * Connect to the video room for the current private area.
   *
   * @param isRetry  Whether this is a reconnection attempt.
   * @param intent   The media state the caller is asking for, when it differs
   *                 from the stored preference. A toggle that starts a
   *                 connection has to pass its value here: the preference state
   *                 update has not been committed yet, so reading it back would
   *                 send the pre-toggle value to the backend.
   */
  const connectToVideoRoom = useCallback(async (isRetry = false, intent?: Partial<VideoPreferences>) => {
    const socket = roomConnection.socket;
    const areaId = stateManager.current.getState().currentPrivateAreaId;

    if (!socket || !areaId) return;

    // Check if can connect
    if (!stateManager.current.canConnectToArea(areaId) && !isRetry) {
      console.warn('🎥 Cannot connect to area:', areaId);
      return;
    }

    // Check if already connecting/connected
    if (connectionManager.current.isConnecting() || connectionManager.current.isConnected()) {
      console.warn('🎥 Already connecting or connected');
      return;
    }

    // Set connection tracking
    stateManager.current.setConnectionInProgress(areaId);

    // Clear previous errors when attempting new connection
    if (!isRetry) {
      clearError();
      stateManager.current.resetConnectionAttempts();
    }

    // Generate unique request ID
    const requestId = `${areaId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    stateManager.current.setCurrentRequestId(requestId);

    // Resolve the intended media state once, here, and reuse it when the token
    // comes back — the backend and the publish path must agree.
    const desiredPreferences: VideoPreferences = { ...desiredPreferencesRef.current, ...intent };
    desiredPreferencesRef.current = desiredPreferences;

    connectionCanPublishRef.current = playerStatusRef.current === 'available';

    console.info('🎥 Requesting video token for area:', areaId, isRetry ? '(retry)' : '', 'requestId:', requestId);

    // Set token timeout
    tokenTimeoutRef.current = setTimeout(() => {
      if (stateManager.current.getState().currentRequestId === requestId) {
        stateManager.current.setConnectionInProgress(null);
        stateManager.current.setCurrentRequestId(null);
        const errorData: VideoError = {
          code: 'TOKEN_TIMEOUT',
          message: 'Video token request timed out. The video server may be unavailable.',
          timestamp: Date.now()
        };
        handleError(errorData);
      }
    }, 10000);

    // Request token from backend
    socket.emit('video:request_token', {
      areaId,
      preferences: desiredPreferences,
      requestId
    });
  }, [roomConnection.socket, handleError, clearError]);

  // Keep the retry path pointed at the current render's connection function.
  useEffect(() => {
    connectToVideoRoomRef.current = connectToVideoRoom;
  }, [connectToVideoRoom]);

  // Drop the LiveKit connection but stay in the private area
  const leaveCall = useCallback(async () => {
    await connectionManager.current.disconnect();
    stateManager.current.setConnected(false);
    stateManager.current.setRoom(null);
  }, []);

  // Handle video token response
  const handleVideoToken = useCallback(async (data: VideoTokenResponse) => {
    // Clear token timeout
    if (tokenTimeoutRef.current) {
      clearTimeout(tokenTimeoutRef.current);
      tokenTimeoutRef.current = null;
    }

    // Check if should handle this token
    if (!stateManager.current.shouldHandleToken(data.requestId)) {
      return;
    }

    console.info('🎥 Received video token for request:', data.requestId);

    try {
      const room = await connectionManager.current.connect(data.url, data.token);
      stateManager.current.setRoom(room);

      // Publish what the user asked for. A failure here is not cosmetic: the
      // device stays off, so the preference is put back and the user is told.
      // Busy and Do Not Disturb never publish, whatever the stored preference.
      const desired = desiredPreferencesRef.current;
      const mayPublish = connectionCanPublishRef.current && playerStatusRef.current === 'available';
      if (room.localParticipant && mayPublish) {
        console.info('🎥 Setting initial media states - camera:', desired.cameraEnabled, 'mic:', desired.micEnabled);

        if (desired.cameraEnabled) {
          try {
            await room.localParticipant.setCameraEnabled(true);
            console.info('🎥 Camera enabled successfully');
          } catch (err) {
            console.error('🎥 Failed to enable camera on connect:', err);
            setCameraEnabled(false);
            handleError(classifyMediaDeviceError(err, 'camera'));
          }
        }

        if (desired.micEnabled) {
          try {
            await room.localParticipant.setMicrophoneEnabled(true);
            console.info('🎥 Microphone enabled successfully');
          } catch (err) {
            console.error('🎥 Failed to enable microphone on connect:', err);
            setMicEnabled(false);
            handleError(classifyMediaDeviceError(err, 'microphone'));
          }
        }
      }

      syncPublishedMedia(room);

      // The status may have changed while the token was in flight, after the
      // status effect below had nothing to act on: catch up now.
      const status = playerStatusRef.current;
      if (status === 'dnd') {
        console.info('🎥 Status changed to DND while connecting, leaving video call');
        await leaveCall();
      } else if (status === 'available' && !connectionCanPublishRef.current) {
        console.info('🎥 Status changed to available while connecting, reconnecting to regain publish permission');
        await leaveCall();
        void connectToVideoRoomRef.current();
      }
    } catch (error) {
      stateManager.current.setConnectionInProgress(null);
      stateManager.current.setCurrentRequestId(null);

      const errorMessage = error instanceof Error ? error.message : 'Failed to connect to video room';
      const errorCode = error instanceof Error && 'code' in error ? (error as any).code : 'CONNECTION_FAILED';
      const errorData: VideoError = {
        code: errorCode,
        message: errorMessage,
        timestamp: Date.now()
      };
      handleError(errorData);
    }
  }, [handleError, setCameraEnabled, setMicEnabled, syncPublishedMedia, leaveCall]);

  /**
   * Handle an error on the socket's generic `error` channel.
   *
   * That channel is shared with the zone, movement, decoration and connection
   * handlers, so most of what arrives on it is none of this context's business.
   * Without a filter an unrelated zone or decoration failure arriving
   * mid-handshake aborted the pending connection and popped "Video Connection
   * Failed" (#77).
   *
   * Two fields mark an error as ours, and they answer different questions:
   *
   * - `scope: 'video'` says *which domain* it belongs to. The backend stamps it
   *   on every video error precisely because some of them cannot carry a
   *   `requestId` — a request rejected before schema validation has no id to
   *   echo back (#84).
   * - `requestId` says *which request* it answers. When one is present it still
   *   has to match the handshake in flight, so a late or duplicate error for an
   *   already-resolved request cannot raise the banner a second time.
   *
   * Hence: a scoped error is ours, but an id, when supplied, must still
   * correlate. Everything else is someone else's problem.
   */
  const handleSocketError = useCallback((data: {
    code: string;
    message?: string;
    requestId?: string;
    scope?: string;
  }) => {
    const { currentRequestId, connectionInProgress } = stateManager.current.getState();

    const isVideoScoped = data?.scope === 'video';
    const answersCurrentRequest = Boolean(data?.requestId) && data.requestId === currentRequestId;
    const isStaleReply = Boolean(data?.requestId) && !answersCurrentRequest;

    if ((!isVideoScoped && !answersCurrentRequest) || isStaleReply) {
      console.debug('🎥 Ignoring socket error that does not belong to the video request in flight:', data?.code);
      return;
    }

    if (tokenTimeoutRef.current) {
      clearTimeout(tokenTimeoutRef.current);
      tokenTimeoutRef.current = null;
    }

    // The request this answers is over either way, so drop its tracking: the
    // 10s token timeout must not fire a second, vaguer error on top of this
    // one, and a handshake left marked as pending strands the state machine.
    //
    // An id-less error cannot be *proved* to belong to the request in flight.
    // Clear anyway: those paths only fire for a payload this client itself
    // malformed, and the alternative — showing the error but leaving the
    // handshake pending — is the hang this filter exists to avoid.
    if (connectionInProgress || currentRequestId) {
      stateManager.current.setConnectionInProgress(null);
      stateManager.current.setCurrentRequestId(null);
    }

    const errorData: VideoError = {
      code: data.code,
      message: data.message || 'Video service error',
      timestamp: Date.now()
    };
    handleError(errorData);
  }, [handleError]);

  // Handle area events
  const handleAreaEntered = useCallback(async (data: { areaId: string; roomId: string }) => {
    console.info('🎥 [EVENT] Entered private area:', data.areaId);

    // If currently connected to a different area, disconnect first
    const currentAreaId = stateManager.current.getState().currentPrivateAreaId;
    if (currentAreaId && currentAreaId !== data.areaId && connectionManager.current.isConnected()) {
      console.info('🎥 Disconnecting from previous area:', currentAreaId);
      await connectionManager.current.disconnect();
    }

    stateManager.current.setCurrentPrivateAreaId(data.areaId);
    // The auto-connect effect will handle connection based on preferences
  }, []);

  const handleAreaLeft = useCallback(async (data: { areaId: string; roomId: string }) => {
    console.info('🎥 [EVENT] Left private area:', data.areaId);
    stateManager.current.setCurrentPrivateAreaId(null);
    stateManager.current.setCurrentRequestId(null);
    await connectionManager.current.disconnect();
  }, []);

  // Monitor room lifecycle. A session taken over by another device or tab
  // (#132) has left the island as far as video goes, although the island stays
  // on screen: the camera and mic must not stay live on a device the user has
  // walked away from, and the session that replaced it joins the call itself.
  const isOnIsland = !!roomConnection.currentRoom && !roomConnection.sessionReplaced;
  useEffect(() => {
    if (isOnIsland) return;

    const manager = connectionManager.current;
    if (manager?.isConnected() || manager?.isConnecting()) {
      console.warn('🎥 [ROOM] Left room, disconnecting all video connections');
      manager.disconnect();
      stateManager.current?.reset();
    }
  }, [isOnIsland]);

  // Auto-connect when entering private area
  useEffect(() => {
    if (!isOnIsland) return;

    const areaId = videoState.currentPrivateAreaId;

    // Clear any pending auto-connect timeout
    if (autoConnectTimeoutRef.current) {
      clearTimeout(autoConnectTimeoutRef.current);
      autoConnectTimeoutRef.current = null;
    }

    // Check conditions for auto-connect
    // Auto-connect if camera is enabled OR autoJoin is enabled (and not in DND mode)
    if (!areaId || !roomConnection.socket || playerStatus === 'dnd') {
      return;
    }

    // Connect if camera OR mic is enabled, OR autoJoin is on
    if (!preferences.cameraEnabled && !preferences.micEnabled && !preferences.autoJoin) {
      return;
    }

    // Check if already connected/connecting
    if (connectionManager.current.isConnected() || connectionManager.current.isConnecting()) {
      console.info('🎥 Auto-connect skipped - already connected/connecting');
      return;
    }

    console.info('🎥 Auto-connect scheduled - camera:', preferences.cameraEnabled, 'mic:', preferences.micEnabled, 'autoJoin:', preferences.autoJoin);

    // Small delay to batch rapid changes and ensure state is settled
    autoConnectTimeoutRef.current = setTimeout(() => {
      if (!connectionManager.current.isConnected() && !connectionManager.current.isConnecting()) {
        console.info('🎥 Auto-connect executing for area:', areaId);
        connectToVideoRoom();
      }
    }, 300); // Increased delay to ensure state is properly updated

    return () => {
      if (autoConnectTimeoutRef.current) {
        clearTimeout(autoConnectTimeoutRef.current);
        autoConnectTimeoutRef.current = null;
      }
    };
  }, [videoState.currentPrivateAreaId, playerStatus, preferences.cameraEnabled, preferences.micEnabled, preferences.autoJoin, isOnIsland, roomConnection.currentRoom, roomConnection.socket, connectToVideoRoom]);

  // Set up socket listeners
  useEffect(() => {
    const socket = roomConnection.socket;
    if (!socket) return;

    socket.on('video:token', handleVideoToken);
    socket.on('error', handleSocketError);
    socket.on('video:area_entered', handleAreaEntered);
    socket.on('video:area_left', handleAreaLeft);

    return () => {
      socket.off('video:token', handleVideoToken);
      socket.off('error', handleSocketError);
      socket.off('video:area_entered', handleAreaEntered);
      socket.off('video:area_left', handleAreaLeft);
    };
  }, [roomConnection.socket, handleVideoToken, handleSocketError, handleAreaEntered, handleAreaLeft]);

  // Apply a status change to a live call: Busy stops publishing mic and camera,
  // Do Not Disturb leaves the call, and returning to Available republishes the
  // stored preferences, which Busy/DND never overwrite (#149).
  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    previousStatusRef.current = playerStatus;
    if (previousStatus === playerStatus) return;

    const manager = connectionManager.current;
    const room = manager.getRoom();
    if (!room || !manager.isConnected()) return;

    async function setMedia(mic: boolean, camera: boolean) {
      const results = await Promise.allSettled([
        room!.localParticipant.setMicrophoneEnabled(mic),
        room!.localParticipant.setCameraEnabled(camera)
      ]);
      results.forEach(result => {
        if (result.status === 'rejected') {
          console.error('🎥 Failed to apply media state for status change:', result.reason);
        }
      });
      syncPublishedMedia(room);
    }

    if (playerStatus === 'dnd') {
      console.info('🎥 Status changed to DND, leaving video call');
      void leaveCall();
    } else if (playerStatus === 'busy') {
      console.info('🎥 Status changed to busy, muting microphone and camera');
      void setMedia(false, false);
    } else if (playerStatus === 'available') {
      if (connectionCanPublishRef.current) {
        console.info('🎥 Status changed to available, restoring media preferences');
        const desired = desiredPreferencesRef.current;
        void setMedia(desired.micEnabled, desired.cameraEnabled);
      } else {
        console.info('🎥 Status changed to available, reconnecting to regain publish permission');
        void leaveCall().then(() => connectToVideoRoom());
      }
    }
  }, [playerStatus, connectToVideoRoom, syncPublishedMedia, leaveCall]);

  // Control functions
  //
  // One shape for both devices: decide the desired value from what is really
  // published (falling back to the stored preference when there is no room),
  // ask LiveKit for it, and only keep the preference if the device agreed.
  const toggleDevice = useCallback(async (device: 'microphone' | 'camera') => {
    if (!canControl) {
      console.info('🎥 Media controls are unavailable in status:', playerStatus);
      return;
    }

    const isMic = device === 'microphone';
    const pendingRef = isMic ? micPendingRef : cameraPendingRef;
    if (pendingRef.current) return;

    const setPending = isMic ? setMicPending : setCameraPending;
    const setPreference = isMic ? setMicEnabled : setCameraEnabled;
    const room = videoState.room;
    const previous = room
      ? (isMic ? publishedMedia.micEnabled : publishedMedia.cameraEnabled)
      : (isMic ? preferences.micEnabled : preferences.cameraEnabled);
    const desired = !previous;

    // Record the intent first so auto-join and the token request agree with it.
    setPreference(desired);

    if (!room) {
      // Nothing is published yet. Turning a device on inside a private area is
      // what starts the call — and the intent has to be handed over explicitly,
      // because the preference update above has not been committed yet.
      if (desired && videoState.currentPrivateAreaId) {
        connectToVideoRoom(false, isMic ? { micEnabled: true } : { cameraEnabled: true });
      }
      return;
    }

    pendingRef.current = true;
    setPending(true);
    try {
      if (isMic) {
        await room.localParticipant.setMicrophoneEnabled(desired);
      } else {
        await room.localParticipant.setCameraEnabled(desired);
      }
    } catch (err) {
      console.error(`🎥 Failed to toggle ${device}:`, err);
      // The device never changed, so neither should the stored intent.
      setPreference(previous);
      handleError(classifyMediaDeviceError(err, device));
    } finally {
      pendingRef.current = false;
      setPending(false);
      // Whatever happened, the button now shows what LiveKit reports.
      syncPublishedMedia(room);
    }
  }, [
    canControl,
    playerStatus,
    videoState.room,
    videoState.currentPrivateAreaId,
    publishedMedia.micEnabled,
    publishedMedia.cameraEnabled,
    preferences.micEnabled,
    preferences.cameraEnabled,
    setMicEnabled,
    setCameraEnabled,
    handleError,
    connectToVideoRoom,
    syncPublishedMedia
  ]);

  const handleToggleMic = useCallback(() => toggleDevice('microphone'), [toggleDevice]);
  const handleToggleCamera = useCallback(() => toggleDevice('camera'), [toggleDevice]);

  // Manual retry function
  const manualRetry = useCallback(() => {
    if (videoState.isConnected || videoState.isReconnecting) return;

    console.info('🎥 Manual retry requested');
    clearError();
    stateManager.current.resetConnectionAttempts();
    connectToVideoRoom();
  }, [videoState.isConnected, videoState.isReconnecting, connectToVideoRoom, clearError]);

  // Clear error after timeout (but only for temporary errors)
  useEffect(() => {
    if (!error || !errorClassification) return;

    // Only auto-clear temporary errors of low severity
    if (errorClassification.isTemporary && errorClassification.severity === 'low') {
      const timeout = setTimeout(() => {
        console.info('🎥 [CONTEXT] Auto-clearing temporary error:', error.code);
        clearError();
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [error, errorClassification, clearError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.warn('🎥 [UNMOUNT] VideoContext unmounting, cleaning up');
      if (autoConnectTimeoutRef.current) {
        clearTimeout(autoConnectTimeoutRef.current);
      }
      if (tokenTimeoutRef.current) {
        clearTimeout(tokenTimeoutRef.current);
      }

      // Cleanup all services
      performanceMonitor.cleanup();
      reconnectionStrategy.current.reset();
      connectionManager.current.cleanup();
      stateManager.current.reset();
    };
  }, []);

  // Simple object for currentPrivateArea (backward compatibility).
  // Memoized on the id: a fresh object literal every render is a changing
  // dependency of `dynamicValue`, which defeated the whole stable/dynamic
  // split and re-rendered every consumer on every provider render (#77).
  const currentPrivateArea = useMemo(
    () => (videoState.currentPrivateAreaId ? { areaId: videoState.currentPrivateAreaId } : null),
    [videoState.currentPrivateAreaId]
  );

  // Helper functions for error information
  const getUserFriendlyError = useCallback(() => {
    return error ? videoErrorHandler.getUserMessage(error) : null;
  }, [error]);

  const getRecoveryActions = useCallback(() => {
    return error ? videoErrorHandler.getRecoveryActions(error) : [];
  }, [error]);

  // Memoize the room reference to prevent unnecessary re-renders
  // Read the room from the state manager, which sets it on connect and clears
  // it on disconnect. Deriving it from `connectionManager.getRoom()` keyed on
  // `isConnected` misses the window between the two (#77) — and audio playback
  // now depends on this value being present for the whole life of the room.
  const roomRef = useMemo(() => videoState.room || null, [videoState.room]);

  // Memoize max reconnect attempts as it rarely changes
  const maxReconnectAttempts = useMemo(() =>
    reconnectionStrategy.current?.getConfig().maxAttempts || 3, []
  );

  // Split the context value into stable and frequently changing parts
  // This allows consumers to subscribe to only what they need
  const stableValue = useMemo(() => ({
    toggleMic: handleToggleMic,
    toggleCamera: handleToggleCamera,
    retryConnection: manualRetry,
    getUserFriendlyError,
    getRecoveryActions,
    maxReconnectAttempts
  }), [handleToggleMic, handleToggleCamera, manualRetry, getUserFriendlyError, getRecoveryActions, maxReconnectAttempts]);

  // Once there is a room, the buttons report what LiveKit is publishing; before
  // that there is nothing to report, so they show the desired value (#75).
  const micEnabled = roomRef ? publishedMedia.micEnabled : preferences.micEnabled;
  const cameraEnabled = roomRef ? publishedMedia.cameraEnabled : preferences.cameraEnabled;

  const dynamicValue = useMemo(() => ({
    isConnected: videoState.isConnected,
    room: roomRef,
    participants: videoState.participants,
    micEnabled,
    cameraEnabled,
    micPending,
    cameraPending,
    canControl,
    currentPrivateArea,
    error,
    isReconnecting: videoState.isReconnecting,
    connectionAttempts: videoState.connectionAttempts,
    errorClassification
  }), [
    videoState.isConnected,
    videoState.participants,
    videoState.isReconnecting,
    videoState.connectionAttempts,
    roomRef,
    micEnabled,
    cameraEnabled,
    micPending,
    cameraPending,
    canControl,
    currentPrivateArea,
    error,
    errorClassification
  ]);

  // Combine stable and dynamic values
  const value: VideoContextState = useMemo(() => ({
    ...stableValue,
    ...dynamicValue
  }), [stableValue, dynamicValue]);

  return (
    <VideoContext.Provider value={value}>
      {children}
    </VideoContext.Provider>
  );
});

